import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  AdminPanelSettings,
  Security,
  Analytics,
  People,
  Settings
} from '@mui/icons-material';
import { simpleAuth } from '../services/simpleAuth';

const AdminLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use simple auth service
      const result = await simpleAuth.signIn(formData.email, formData.password);
      
      if (result.success && result.user) {
        // Check if user is admin
        if (result.user.role === 'admin') {
          localStorage.setItem('adminToken', result.token);
          localStorage.setItem('adminData', JSON.stringify(result.user));
          onLogin(result.user, result.token);
        } else {
          setError('Access denied. Admin credentials required.');
        }
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoAdmin = () => {
    const demoAdmin = {
      id: 'admin-1',
      email: 'admin@example.com',
      name: 'Admin User',
      company: 'Platform Admin',
      role: 'admin',
      subscription_tier: 'admin',
      subscription_status: 'active',
      payment_verified: true
    };
    const token = `demo-admin-token-${Date.now()}`;
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminData', JSON.stringify(demoAdmin));
    onLogin(demoAdmin, token);
  };

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Box sx={{ backgroundColor: 'primary.main', borderRadius: '50%', p: 1, mb: 2 }}>
            <AdminPanelSettings sx={{ color: 'white', fontSize: 40 }} />
          </Box>
          
          <Typography component="h1" variant="h4" gutterBottom>
            Admin Portal
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Access platform administration and management tools
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 400 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              autoFocus
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In as Admin'}
            </Button>
          </Box>

          <Divider sx={{ width: '100%', my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Button
            variant="outlined"
            onClick={handleDemoAdmin}
            sx={{ mb: 2 }}
          >
            Use Demo Admin Account
          </Button>

          <Box sx={{ mt: 3, width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Admin Features
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <People sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">Client Management</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage all client accounts and subscriptions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Analytics sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">Platform Analytics</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monitor platform performance and revenue
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Security sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">Security Controls</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage access controls and security settings
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Settings sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">System Administration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure platform settings and integrations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Demo Admin Credentials:
            </Typography>
            <Typography variant="body2" color="primary" sx={{ fontFamily: 'monospace' }}>
              admin@example.com / admin123
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminLogin; 